import {ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit} from '@angular/core';
import {toObservable} from '@angular/core/rxjs-interop';
import {Hardware} from '../../../types/hardware';
import {BehaviorSubject, combineLatest, Observable, ReplaySubject} from 'rxjs';
import {map} from 'rxjs/operators';
import {Entity, Store} from '@softline/core';
import {
  HardwareManagementCommentsStore,
  HardwareManagementStore,
  SOFTLINE_FEATURE_HARDWARE_MANAGEMENT,
  SOFTLINE_FEATURE_HARDWARE_MANAGEMENT_COMMENTS,
  SOFTLINE_PERMISSION_HARDWARE_MANAGEMENT_EDITING
} from '../../../hardware-management.shared';
import {showRequestErrors} from '@softline/application';
import {
  MessageBarStore,
  ModalStore,
  QuestionResult,
  SOFTLINE_FEATURE_MESSAGE_BAR,
  SOFTLINE_FEATURE_MODAL} from '@softline/ui-core';
import {ReactiveFormsModule, UntypedFormGroup} from '@angular/forms';
import {authorizationFn} from '@softline/auth';
import {Comment} from '../../../types/comment';
import {CreateCommentDialogComponent} from '../../../dialogs/create-comment-dialog/create-comment-dialog.component';
import {CommonModule} from '@angular/common';
import {DynamicModule} from '@softline/dynamic';
import {
  BetriebssystemInputComponent
} from '../../../components/inputs/betriebssystem-input/betriebssystem-input.component';
import {GarantietypInputComponent} from '../../../components/inputs/garantietyp-input/garantietyp-input.component';
import {HerstellerInputComponent} from '../../../components/inputs/hersteller-input/hersteller-input.component';
import {ProduktInputComponent} from '../../../components/inputs/produkt-input/produkt-input.component';
import {SubnetzmaskeInputComponent} from '../../../components/inputs/subnetzmaske-input/subnetzmaske-input.component';
import {SupporttypInputComponent} from '../../../components/inputs/supporttyp-input/supporttyp-input.component';
import {TypInputComponent} from '../../../components/inputs/typ-input/typ-input.component';
import { HardwareFormDirective } from "../../../directives/hardware-form.directive";
import { KtostammStandortInputComponent } from '../../../components/inputs/ktostamm-standort-input/ktostamm-standort-input.component';

interface ViewModel {
  editing: boolean;
  formOpened: boolean;
  syncedHardware: Hardware | null | undefined;
  hasEditingPermission: boolean;
  comments: Comment[];
}

import { DateInputComponent } from '@softline/ui-core/date-input';
import { InputComponent } from '@softline/ui-core';
import { ToggleSwitchComponent } from '@softline/ui-core';
import { TranslatePipe } from '@softline/ui-core';
@Component({
    selector: 'soft-hardware-details',
    imports: [CommonModule, DynamicModule, ReactiveFormsModule,
        BetriebssystemInputComponent, GarantietypInputComponent, HerstellerInputComponent,
        ProduktInputComponent, SubnetzmaskeInputComponent, SupporttypInputComponent,
        TypInputComponent, HardwareFormDirective, KtostammStandortInputComponent,
    DateInputComponent,
    InputComponent,
    ToggleSwitchComponent,
    TranslatePipe],
    templateUrl: './hardware-details.component.html',
    styleUrls: ['./hardware-details.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class HardwareDetailsComponent {

  @Input() isOnlyView = false;

  private readonly isAuthorized = authorizationFn();
  private readonly syncedHardware$ = new BehaviorSubject<Hardware | undefined | null>(null);

  readonly formOpened$ = new BehaviorSubject(true);
  readonly editing$ = new BehaviorSubject(false);

  readonly updateFormValue$ = new ReplaySubject<Hardware | undefined | null>();

  readonly hasEditingPermission = this.isAuthorized(SOFTLINE_PERMISSION_HARDWARE_MANAGEMENT_EDITING);
  readonly hasEditingPermission$ = toObservable(this.hasEditingPermission);

  readonly comments$ = this.store.observe(
    SOFTLINE_FEATURE_HARDWARE_MANAGEMENT_COMMENTS,
    HardwareManagementCommentsStore.getters.all
  );

  readonly vm$: Observable<ViewModel> = combineLatest([
    this.editing$,
    this.formOpened$,
    this.hasEditingPermission$,
    this.syncedHardware$,
    this.comments$
  ]).pipe(
    map(([editing, formOpened, hasEditingPermission, syncedHardware, comments]) => ({
      editing,
      formOpened,
      hasEditingPermission,
      syncedHardware,
      comments: comments as Comment[]
    }))
  );

  @Input()
  set hardware(value: Hardware | undefined | null) {
    this.syncedHardware$.next(value);
    this.updateFormValue$.next(value);
    if (value) {
      this.store.dispatch(
        SOFTLINE_FEATURE_HARDWARE_MANAGEMENT_COMMENTS,
        HardwareManagementCommentsStore.actions.loadMany,
        { clear: true, pathParams: { idhardware: value.id } }
      );
    }
  }

  constructor(private store: Store) { }

  async cancelEditing(form: UntypedFormGroup): Promise<void> {
    if (!form.dirty || !form.touched) {
      this.resetForm(form);
      return;
    }

    const result: QuestionResult | 'DISMISSED' = await this.store.dispatch(
      SOFTLINE_FEATURE_MODAL,
      ModalStore.actions.ask,
      {
        title: '#HARDWARE_MANAGEMENT.DIALOGS.CANCEL_EDITING.TITLE',
        question: '#HARDWARE_MANAGEMENT.DIALOGS.CANCEL_EDITING.TEXT' }
    );

    if (!result || result !== 'YES') {
      return;
    }

    this.resetForm(form);
  }

  async saveEditing(form: UntypedFormGroup): Promise<void> {
    try {
      await this.store.dispatch(
        SOFTLINE_FEATURE_HARDWARE_MANAGEMENT,
        HardwareManagementStore.actions.update,
        { entity: form.value }
      );

      await this.store.dispatch(
        SOFTLINE_FEATURE_MESSAGE_BAR,
        MessageBarStore.actions.success,
        '#HARDWARE_MANAGEMENT.MESSAGES.UPDATE_HARDWARE_SUCCESS'
      );

      this.resetForm(form);
    } catch (e) {
      showRequestErrors(this.store, e);
    }
  }

  trackByEntityFn<T extends Entity>(_: number, entity: T) {
    return entity.id;
  }

  async showCreateCommentDialog(kundehardware: Hardware | undefined | null): Promise<void> {
    if(!kundehardware)
      return;
    const result = await this.store.dispatch(
      SOFTLINE_FEATURE_MODAL,
      ModalStore.actions.open<{ content: string } | undefined, {}>(),
      {
        id: 'HARDWARE_MANAGEMENT_CREATE_COMMENT',
        component: CreateCommentDialogComponent,
        dismiss: true }
    );

    if (!result || result === 'DISMISSED' || !result?.content) {
      return;
    }

    const commentEntity: Comment = {
      id: 0,
      kommentar: result.content,
      zeit: new Date().toISOString(),
      kundehardware,
      kontaktbw: null
    };

    try {
      await this.store.dispatch(
        SOFTLINE_FEATURE_HARDWARE_MANAGEMENT_COMMENTS,
        HardwareManagementCommentsStore.actions.create,
        { entity: commentEntity, pathParams: { idhardware: kundehardware.id } }
      );
    } catch (e) {
      showRequestErrors(this.store, e);
    }
  }

  async showEditCommentDialog(comment: Comment, kundehardware: Hardware | undefined | null): Promise<void> {
    if(!kundehardware)
      return;
    const result = await this.store.dispatch(
      SOFTLINE_FEATURE_MODAL,
      ModalStore.actions.open<{ content: string } | undefined, { comment?: Comment | undefined }>(),
      {
        id: 'HARDWARE_MANAGEMENT_CREATE_COMMENT',
        component: CreateCommentDialogComponent as any,
        dismiss: true,
        data: {
          comment
        }
      }
    );

    if (!result || result === 'DISMISSED' || !result?.content) {
      return;
    }

    const updatedComment: Comment = {
      ...comment,
      kommentar: result?.content ?? ''
    };

    try {
      await this.store.dispatch(
        SOFTLINE_FEATURE_HARDWARE_MANAGEMENT_COMMENTS,
        HardwareManagementCommentsStore.actions.update,
        { entity: updatedComment, pathParams: { idhardware: kundehardware.id } }
      );
    } catch (e) {
      showRequestErrors(this.store, e);
    }
  }

  async removeComment(comment: Comment, hardware: Hardware | undefined | null): Promise<void> {
    if(!hardware)
      return;
    const result = await this.store.dispatch(
      SOFTLINE_FEATURE_MODAL,
      ModalStore.actions.ask,
      {
        title: 'Löschen',
        question: 'Wollen Sie den Kommentar wirklich unwiderruflich löschen?'
      }
    );

    if (!result || result !== 'YES') {
      return;
    }

    try {
      await this.store.dispatch(
        SOFTLINE_FEATURE_HARDWARE_MANAGEMENT_COMMENTS,
        HardwareManagementCommentsStore.actions.delete,
        { entity: comment, pathParams: { idhardware: hardware.id, id: comment.id } }
      );
    } catch (e) {
      showRequestErrors(this.store, e);
    }
  }

  private resetForm(form: UntypedFormGroup) {
    this.updateFormValue$.next(this.syncedHardware$.value);
    form.reset(this.syncedHardware$.value);
    form.disable();
    this.editing$.next(false);
  }
}
